import React, { useContext } from 'react';
import _ from 'lodash';
import StatsLeaders from './Leaders/StatsLeaders';
import { usePlayersStats } from '../../hooks/player';
import { useTeamsStats } from '../../hooks/teamStats';
import { getLeadersWithTopListNumber } from '../../hooks/leaders';
import { DataContext } from '../../context/data-context';
import { usePlayerFilterOptions } from './FilterOptions/PlayerFilterOptions';
import { useTeamFilterOptions } from './FilterOptions/TeamFilterOptions';
import Loader from '../Loader';
import Carousel from '../Carousel';
import { useMatchesMinimized } from '../../hooks/matches';
import { addFormToRow } from '../../utils/standings';
import { useStandings } from '../../hooks/standings';

function Overview() {
  const [playerFilterOptions] = usePlayerFilterOptions();
  const [teamFilterOptions] = useTeamFilterOptions();
  const { currentSeason } = useContext(DataContext);

  const [allMatches, allMatchesIsLoading] = useMatchesMinimized(currentSeason);
  const [playersStats, playersStatsLoading] = usePlayersStats(currentSeason);
  const [teamsStatsRaw, teamsStatsLoading] = useTeamsStats(currentSeason);

  const [standings, standingsLoading] = useStandings(currentSeason);

  const teamsStats = (() => {
    if (allMatchesIsLoading || teamsStatsLoading || standingsLoading) {
      return null;
    }

    const finishedMatches = (allMatches || []).filter(
      (match) => match.status === 'FINISHED'
    );
    const standingsObj = Object.values(standings || {}).reduce(
      (acc, teamStandings) => {
        return {
          ...acc,
          [teamStandings.teamAbbrv]: (teamStandings.stats || []).reduce(
            (a, s) => {
              return {
                ...a,
                [s.name]: s.value ? parseInt(s.value, 10) : 0,
              };
            },
            {}
          ),
        };
      },
      {}
    );
    return teamsStatsRaw.map((teamStats) => {
      const teamStatsWithForm = addFormToRow(finishedMatches)(teamStats);
      const goalsConceded = standingsObj?.[teamStats.abbrv]?.ga || null;

      return {
        ...teamStatsWithForm,
        goalsConceded,
      };
    });
  })();

  const player = playersStats
    ? [
        {
          data: getLeadersWithTopListNumber(
            playersStats.map((x) => ({ ...x, stat: x.goals }))
          ).splice(0, 10),
          type: 'goals',
          title: 'Mål',
        },
        {
          data: getLeadersWithTopListNumber(
            playersStats.map((x) => ({ ...x, stat: x.assists }))
          ).splice(0, 10),
          type: 'assists',
          title: 'Assist',
        },
        {
          data: getLeadersWithTopListNumber(
            playersStats.map((x) => ({ ...x, stat: x.yellowCards }))
          ).splice(0, 10),
          type: 'yellowCards',
          title: 'Gula kort',
        },
        {
          data: getLeadersWithTopListNumber(
            playersStats.map((x) => ({ ...x, stat: x.highestSpeed }))
          ).splice(0, 10),

          type: 'highestSpeed',
          title: 'Högsta hastighet',
        },
      ]
    : [];

  const team = teamsStats
    ? [
        {
          data: getLeadersWithTopListNumber(
            teamsStats.map((x) => ({
              ...x,
              stat: x.formPerformance,
            }))
          ).splice(0, 10),
          type: 'formPerformance',
          title: 'Form',
        },
        {
          data: getLeadersWithTopListNumber(
            teamsStats.map((x) => ({
              ...x,
              stat: x.goalsConceded,
            })),
            'ASC'
          ).splice(0, 10),
          type: 'goalsConceded',
          title: 'Insläppta mål',
        },
        {
          data: getLeadersWithTopListNumber(
            teamsStats.map((x) => ({ ...x, stat: x.shotsOnTarget }))
          ).splice(0, 10),
          type: 'shotsOnTarget',
          title: 'Avslut på mål',
        },
        {
          data: getLeadersWithTopListNumber(
            teamsStats.map((x) => ({ ...x, stat: x.averageAttendees }))
          ).splice(0, 10),
          type: 'averageAttendees',
          title: 'Publiksnitt',
        },
        {
          data: getLeadersWithTopListNumber(
            teamsStats.map((x) => ({ ...x, stat: x.setPieceGoals }))
          ).splice(0, 10),
          type: 'setPieceGoals',
          title: 'Mål på fasta situationer',
        },
        {
          data: getLeadersWithTopListNumber(
            teamsStats.map((x) => ({ ...x, stat: x.goals }))
          ).splice(0, 10),
          type: 'goals',
          title: 'Mål',
        },
        {
          data: getLeadersWithTopListNumber(
            teamsStats.map((x) => ({ ...x, stat: x.yellowCards }))
          ).splice(0, 10),
          type: 'yellowCards',
          title: 'Gula kort',
        },
        {
          data: getLeadersWithTopListNumber(
            teamsStats.map((x) => ({ ...x, stat: x.averagePossesion }))
          ).splice(0, 10),
          type: 'averagePossesion',
          title: 'Genomsnitt bollinnehav',
        },
        {
          data: getLeadersWithTopListNumber(
            teamsStats.map((x) => ({
              ...x,
              stat: _.round(x.totalDistance, 0),
            }))
          ).splice(0, 10),
          type: 'totalDistance',
          title: 'Löpmeter (m)',
        },
      ]
    : [];

  const toRenderPlayer = player.filter((item) =>
    playerFilterOptions.map((x) => x.key === item.type).includes(true)
  );
  const toRenderTeam = team.filter((item) =>
    teamFilterOptions.map((x) => x.key === item.type).includes(true)
  );

  return (
    <div className="tabs-content">
      <div className="row justify-content-center">
        <div className="col-12 tabs-column tabs-column__overview-tab">
          <h2 className="heading-h6 heading-lg-h3 my-3 my-lg-4 px-lg-4 px-2">
            SPELARE
          </h2>
          <Carousel
            isReady={!playersStatsLoading}
            itemSelector=".carousel--item"
            numberOfItems={toRenderPlayer.length}
            containerClassName="overflow-x-auto d-flex flex-nowrap row article-scroll__container--carousel"
          >
            {toRenderPlayer.map((d) => (
              <div
                key={`${d.type}`}
                className="carousel--item scrollable-content-width col-4 mb-3 ms-2 ms-lg-0 me-3 me-ls-0 me-lg-0 px-0 px-lg-2"
              >
                <div className="data-container">
                  <div className="px-4 pt-4">
                    <h5 className="heading-h5">{d.title}</h5>
                  </div>
                  {playersStatsLoading ? (
                    <Loader />
                  ) : (
                    <StatsLeaders type={d.type} data={d.data} />
                  )}
                </div>
              </div>
            ))}
          </Carousel>
        </div>
      </div>
      <div className="row justify-content-center">
        <div className="col-12 tabs-column tabs-column__overview-tab">
          <h2 className="heading-h6 heading-lg-h3 my-3 my-lg-4 px-lg-4 px-2">
            LAG
          </h2>
          <Carousel
            isReady={!teamsStatsLoading}
            itemSelector=".carousel--item"
            numberOfItems={toRenderTeam.length}
            containerClassName="overflow-x-auto d-flex flex-nowrap row article-scroll__container--carousel"
          >
            {toRenderTeam.map((d) => (
              <div
                key={`${d.type}`}
                className="carousel--item scrollable-content-width col-4 mb-3 ms-2 ms-lg-0 me-3 me-ls-0 me-lg-0 px-0 px-lg-2"
              >
                <div className="data-container">
                  <div className="px-4 pt-4">
                    <h5 className="heading-h5">{d.title}</h5>
                  </div>
                  {teamsStatsLoading ? (
                    <Loader />
                  ) : (
                    <StatsLeaders type={`teams_${d.type}`} data={d.data} />
                  )}
                </div>
              </div>
            ))}
          </Carousel>
        </div>
      </div>
    </div>
  );
}

export default Overview;
